import {
  resolveRunClassName,
  resolveRunInteraction,
} from "@nijiurachan/pua-command";
import { useState } from "react";

interface Props {
  /** そのランに効く装飾クラス。外側の装飾から順に並ぶ (tokenize の契約) */
  classNames: readonly string[];
  text: string;
}

/**
 * PUA装飾ラン1個の描画。
 *
 * どの装飾かを一切知らない汎用実装。クラス名と振る舞いは pua-command の宣言
 * (definitions.ts の interaction) から導出し、見た目は pua-command の pua.css が持つ。
 *
 * 装飾を追加・変更してもこのファイルは編集しない。編集が要るのは PuaInteraction に
 * 新しい kind を足すときだけで、そのときは下の switch がコンパイルエラーで漏れを知らせる。
 */
export const PuaRun: React.FunctionComponent<Props> = ({
  classNames,
  text,
}: Props) => {
  const className = resolveRunClassName(classNames);
  const interaction = resolveRunInteraction(classNames);
  if (interaction === null) {
    return <span className={className}>{text}</span>;
  }
  switch (interaction.kind) {
    case "toggle":
      return (
        <PuaToggleRun
          className={className}
          text={text}
          hint={interaction.hint}
          initialPressed={interaction.initialPressed}
        />
      );
  }
};

interface ToggleProps {
  className: string;
  text: string;
  hint: string;
  initialPressed: boolean;
}

/**
 * kind:"toggle" の汎用実装。トグルボタンとしてのDOM表現 (role/aria-pressed/キー操作) は
 * kind に対して一意に決まるので、装飾ごとではなくここで一度だけ与える。
 *
 * 状態はこのコンポーネントのローカルなので、同一スレに同種のランが複数あっても
 * 互いに波及しない。
 */
const PuaToggleRun: React.FunctionComponent<ToggleProps> = ({
  className,
  text,
  hint,
  initialPressed,
}: ToggleProps) => {
  const [pressed, setPressed] = useState(initialPressed);
  const toggle = (): void => setPressed((current) => !current);

  return (
    // biome-ignore lint/a11y/useSemanticElements: <button>はブラウザ既定で内部テキストをドラッグ選択できず、選択して読む従来操作を壊すため、選択可能な<span>にボタンのセマンティクスを与える
    <span
      className={className}
      role="button"
      tabIndex={0}
      title={hint}
      aria-pressed={pressed}
      onClick={(): void => {
        // ドラッグ選択の直後のclickで勝手に切り替わらないようにする
        if (hasActiveTextSelection()) return;
        toggle();
      }}
      onKeyDown={(event: React.KeyboardEvent<HTMLSpanElement>): void => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          toggle();
        }
      }}
    >
      {text}
    </span>
  );
};

/** ドラッグ選択が残っているか。SSR描画やgetSelection非対応環境では false を返す */
function hasActiveTextSelection(): boolean {
  if (
    typeof window === "undefined" ||
    typeof window.getSelection !== "function"
  ) {
    return false;
  }
  const selection = window.getSelection();
  return selection !== null && !selection.isCollapsed;
}
